import { clsx } from 'clsx';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';

export type TableProps = ComponentProps<'table'> & {
  /** Classes for the scroll container (e.g. a max height so the sticky header engages). */
  containerClassName?: string;
};

/**
 * Dense data table inside a bordered scroll container. The header row is sticky within the container;
 * give the container a height (`containerClassName="max-h-[calc(100vh-14rem)]"`) for long lists.
 */
export function Table({ className, containerClassName, ...rest }: TableProps) {
  return (
    <div className={clsx('relative overflow-auto rounded-lg border border-slate-200 bg-surface', containerClassName)}>
      <table className={clsx('w-full border-separate border-spacing-0 text-left text-[13px]', className)} {...rest} />
    </div>
  );
}

export function THead({ className, ...rest }: ComponentProps<'thead'>) {
  return <thead className={clsx('sticky top-0 z-10', className)} {...rest} />;
}

export function TBody({ className, ...rest }: ComponentProps<'tbody'>) {
  return <tbody className={clsx('[&>tr:last-child>td]:border-b-0', className)} {...rest} />;
}

export type TRProps = ComponentProps<'tr'> & {
  /** Highlight as selected (checkbox selection). */
  selected?: boolean;
  /** Hover affordance + pointer for clickable rows. */
  interactive?: boolean;
};

export function TR({ className, selected, interactive, ...rest }: TRProps) {
  return (
    <tr
      aria-selected={selected || undefined}
      className={clsx(
        'group',
        selected ? 'bg-ember-50/60' : interactive && 'hover:bg-slate-50',
        interactive && 'cursor-pointer',
        className,
      )}
      {...rest}
    />
  );
}

export type SortDirection = 'asc' | 'desc';

export type THProps = Omit<ComponentProps<'th'>, 'onClick'> & {
  /** Current sort of this column: direction when sorted by it, otherwise null. Presence enables sorting. */
  sort?: SortDirection | null;
  /** Called when the header is activated; the parent decides the next direction. */
  onSort?: () => void;
  align?: 'left' | 'right' | 'center';
};

const ALIGN = { left: 'text-left', right: 'text-right', center: 'text-center' } as const;

export function TH({ className, sort, onSort, align = 'left', children, ...rest }: THProps) {
  const sortable = onSort !== undefined;
  const SortIcon = sort === 'asc' ? ArrowUp : sort === 'desc' ? ArrowDown : ChevronsUpDown;
  return (
    <th
      scope="col"
      aria-sort={sortable ? (sort === 'asc' ? 'ascending' : sort === 'desc' ? 'descending' : 'none') : undefined}
      className={clsx(
        'h-8 border-b border-slate-200 bg-slate-50 px-3 text-2xs font-semibold tracking-wide whitespace-nowrap text-slate-500 uppercase first:rounded-tl-lg last:rounded-tr-lg',
        ALIGN[align],
        className,
      )}
      {...rest}
    >
      {sortable ? (
        <button
          type="button"
          onClick={onSort}
          className={clsx(
            'group/sort -mx-1 inline-flex items-center gap-1 rounded px-1 py-0.5 uppercase hover:text-slate-900 focus-visible:outline-2 focus-visible:outline-ember-500',
            sort && 'text-slate-900',
            align === 'right' && 'flex-row-reverse',
          )}
        >
          {children}
          <SortIcon
            size={12}
            aria-hidden
            className={clsx(sort ? 'text-ember-600' : 'text-slate-300 group-hover/sort:text-slate-500')}
          />
        </button>
      ) : (
        children
      )}
    </th>
  );
}

export type TDProps = ComponentProps<'td'> & { align?: 'left' | 'right' | 'center'; numeric?: boolean };

export function TD({ className, align = 'left', numeric, ...rest }: TDProps) {
  return (
    <td
      className={clsx(
        'h-10 border-b border-slate-100 px-3 align-middle text-slate-700',
        ALIGN[numeric ? 'right' : align],
        numeric && 'tabular',
        className,
      )}
      {...rest}
    />
  );
}

/** Row spanning all columns, for loading/empty states inside a table body. */
export function TableMessageRow({ colSpan, children }: { colSpan: number; children: ReactNode }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-3 py-10 text-center text-[13px] text-slate-500">
        {children}
      </td>
    </tr>
  );
}
